import React, { useEffect, useState, useMemo } from "react";
import { Box, CircularProgress, Paper } from "@mui/material";
import { useNavigate } from "react-router-dom";
import Header from "../HeaderPage";
import logo from "../../asset/logo32.png";
import { decrypt } from "../../components/cryptoUtil";
// Icons
import {
  Dashboard as DashboardIcon,
  BarChart as BarChartIcon,
  PieChart as PieChartIcon,
  FileUpload as FileUploadIcon,
  Settings as SettingsIcon,
  ConstructionSharp as ConstructionSharpIcon,
} from "@mui/icons-material";

// Dashboards

import DefaultDashboard from "./DefaultDashboard";
import HospitolDashBoard from "../../HospitalPages/HospitalMainDashBoard";
import SchoolDashBoard from "./SchoolDashBoard";
import RIBDashBoard from "./RIBDashBoard";


import NewUserCreation from "../UserManagement/NewUserCreation";
import UpdateUser from "../UserManagement/UpdateUser";
import UserDetails from "../UserManagement/UserDetails";
import ChangePassword from "../UserManagement/ChangePassword";

import ExcelUploader from "../ExcelUploader/ExcelUploader";
import QueryUploader from "../QueryUploader/QueryUploader";
import DocumentUploader from "../DocumentUploader/DocumentUploader";
import RichTextEditor from "../RichTextEditor/RichTextEditor";
import FileExplorer from "../FolderViewer/FolderViewer";
import SettingTools from "../SettingTools/SettingTools";

// Project-specific Menus and Content
import { ribMenus }  from "../../Menu/rib/ribMenu";
import { schMenus } from "../../Menu/sch/schMenu";
import { ribContent } from "../../Menu/rib/ribContent";
import { schContent } from "../../Menu/sch/schContent";

import { hmsMenus } from "../../Menu/hms/hmsMenu"
import { hmsContent } from "../../Menu/hms/hmsContent";

// ---------------- Permission Utilities ----------------
const parseMenuAllowed = (raw) => {
  const rawAllowed = JSON.parse(decrypt(localStorage.getItem("tokenData")));
  const userRole = rawAllowed?.userId;

  if (userRole === "admin") return "ALL"; // Super admin

  let allowed = [];
  if (!raw) return ["Dashboard"];

  if (Array.isArray(raw)) allowed = raw;
  else if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      allowed = Array.isArray(parsed) ? parsed : raw.split(",").map((i) => i.trim());
    } catch {
      allowed = raw.split(",").map((i) => i.trim());
    }
  }

  const exclusions = ["ALL", "Utilities", "Settings"];
  allowed = allowed.filter((item) => !exclusions.includes(item));
  if (!allowed.includes("Dashboard")) allowed.unshift("Dashboard");

  return allowed;
};

const filterMenuItems = (menuItems, allowed) => {
  if (allowed === "ALL") return menuItems;

  return menuItems
    .filter((menu) =>
      allowed.some(
        (perm) =>
          perm === menu.label || (perm.startsWith(menu.label + ":") && menu.children)
      )
    )
    .map((menu) => {
      const childAllowed = allowed
        .filter((perm) => perm.startsWith(menu.label + ":"))
        .map((perm) => perm.split(":")[1]);

      if (menu.children && childAllowed.length > 0) {
        return { ...menu, children: menu.children.filter((c) => childAllowed.includes(c.label)) };
      }
      return menu;
    });
};

// ---------------- DashboardPage Component ----------------
function DashboardPage() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedMenu, setSelectedMenu] = useState("Dashboard");
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [darkMode, setDarkMode] = useState(false);
  const navigate = useNavigate();

  // Load user data from token
  useEffect(() => {
    const tokenData = JSON.parse(decrypt(localStorage.getItem("tokenData")));
    //console.log(tokenData.softgroup);
      setUser({
      userfullname: tokenData.userfullname,
      userrole: tokenData.userrole,
      uname: tokenData.userId,
      prefix: tokenData.softgroup,
    });
    setLoading(false);
  }, [navigate]);

  const SoftDetails = JSON.parse(decrypt(localStorage.getItem("tokenData")));
  const prefix = SoftDetails.softgroup; // e.g. "rib" or "sch"

  // Build Menu based on project
  const menuItems = [
    { label: "Dashboard", icon: <DashboardIcon /> , divider: true },
    ...(prefix === "rib" ? ribMenus : []),
    ...(prefix === "sch" ? schMenus : []),
    ...(prefix === "hms" ? hmsMenus : []),
    {
      label: "User Management" , divider: false,
    icon: <span style={{ fontSize: "1.5rem" }}>👨‍💼</span>,
      children: [
        { label: "New User Creation", icon: <BarChartIcon /> },
        { label: "Update User", icon: <BarChartIcon /> },
      ],
    },
    {
      label: "Utilities",
      icon: <ConstructionSharpIcon />,
      children: [
        { label: "Import Excel/CSV File", icon: <BarChartIcon /> },
        { label: "Import Document Files", icon: <BarChartIcon /> },
        { label: "Data Explore from Query", icon: <BarChartIcon /> },
        { label: "Rich Text Box", icon: <BarChartIcon /> },
        { label: "File Explorer", icon: <BarChartIcon /> },
      ],
    },
    { label: "Settings", icon: <SettingsIcon /> },

   
  ];

  // Permission filtering
  const rawAllowed = JSON.parse(decrypt(localStorage.getItem("tokenData")))?.menuallowed;
  const allowedMenus = parseMenuAllowed(rawAllowed);
  const filteredMenuItems = useMemo(() => filterMenuItems(menuItems, allowedMenus), [allowedMenus]);

  // Role-based dashboard
  const roleComponentMap = {
    


    "rib": RIBDashBoard,
    "sch": SchoolDashBoard,
    "hms": HospitolDashBoard,
  };
  const DashboardComponent = roleComponentMap[user?.prefix] || DefaultDashboard;

  // Project-specific content components
  const contentComponents = {
    "Dashboard": DashboardComponent,
    ...(prefix === "rib" ? ribContent : {}),
    ...(prefix === "sch" ? schContent : {}),
    ...(prefix === "hms" ? hmsContent : {}),
    "Import Excel/CSV File": ExcelUploader,
    "Data Explore from Query": QueryUploader,
    "Import Document Files": DocumentUploader,
    "Rich Text Box": RichTextEditor,
    "File Explorer": FileExplorer,
    "New User Creation": NewUserCreation,
    "Update User": UpdateUser,
    "User Details": UserDetails,
    "Change Password": ChangePassword,
    Settings: SettingTools,
  };

  const SelectedComponent =
    contentComponents[selectedMenu] || (() => <div style={{ padding: 20 }}>Select a menu</div>);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <>
      <Header
        // firmName={`${SoftDetails?.softwarename} - ${SoftDetails?.sofwaredetailsname}`}
         firmName={`${SoftDetails?.sofwaredetailsname}`}
        logoUrl={logo}
        username={user?.userfullname || "User"}
        menuItems={filteredMenuItems}
        onMenuSelect={(item) => {
          setSelectedMenu(item);
          setDrawerOpen(false);
        }}
        drawerOpen={drawerOpen}
        setDrawerOpen={setDrawerOpen}
        darkMode={darkMode}
        setDarkMode={setDarkMode}
      />

      <Box
        sx={{
          // marginLeft: { xs: 0, sm: drawerOpen ? "250px" : 0 },
         marginLeft: { xs: 0, sm:  0 },
         //width: { xs: "100%", sm: drawerOpen ? "calc(100% - 250px)" : "100%" },
    width: { xs: "100%", sm:  "100%" },
    bgcolor: darkMode ? "grey.900" : "#f5f5f5",
    color: darkMode ? "grey.100" : "text.primary",
    
        }}
      >
        <Paper
          sx={{
            padding: 2,
            bgcolor: darkMode ? "grey.800" : "white",
            borderRadius: 2,
            
            
          }}
        >
          <SelectedComponent />

          
        </Paper>

         
      </Box>
    </>
  );
}

export default DashboardPage;
